/**
 * Data layer for the COMPANY panel's Self-Assessment section.
 *
 * The source is the assessment run — one row per person per questionnaire. Two
 * things about it shape every number here:
 *
 *  - A run is created the moment the first question is answered and only flips
 *    to completed at the end, so half-finished runs are real drop-offs and the
 *    start → finish funnel is a genuine measurement, not an estimate.
 *  - There are TWO kinds of outcome. Scored assessments return a percentage,
 *    which the app itself reads as one of four quartiles; attachment
 *    assessments return a style (Secure / Avoidant / Anxious / Disorganised)
 *    and have no score at all. They are reported side by side and never summed.
 *
 * A quartile is not a grade: 80% on a resilience questionnaire and 80% on a
 * burnout one mean opposite things, so the company-wide spread is context for
 * the volume and the meaning lives in the per-assessment breakdown, where every
 * bar belongs to one questionnaire.
 *
 * The `user_details` captured before a run — date of birth, ethnicity, job
 * role, and answers about medication and recent GP visits — is never fetched by
 * any endpoint below, so it cannot reach this layer.
 *
 * Backed by the company/v1 self-assessment-hub endpoints (token-scoped to the
 * company; the B2B/Members toggle is appended by api/companyApi.js). Every
 * fetcher takes an optional `department` so the section's filter can narrow the
 * whole page.
 *
 * The adapter is exported so the super-admin Self-Assessment hub, served by the
 * same builders, can reuse the field mapping rather than restating it.
 */

import {
  getCompanySelfAssessmentDashboard,
  getCompanySelfAssessmentUsers,
  getCompanySelfAssessmentUserDetail
} from '../../../../api/companyApi';
import { unwrap } from '../../../SleepHub/sleepHubApi';

/** Assessment block shared by every Self-Assessment dashboard. */
export const adaptSelfAssessments = (data) => ({
  // Reach and volume
  totalAssessments: data.totalAssessments ?? 0,
  assessmentsUsed: data.assessmentsUsed ?? 0,
  totalCompletions: data.totalCompletions ?? 0,
  usersCompleted: data.usersCompleted ?? 0,
  usersStarted: data.usersStarted ?? 0,
  repeatUsers: data.repeatUsers ?? 0,
  avgPerUser: data.avgPerUser ?? 0,
  firstCompletionDate: data.firstCompletionDate || null,
  lastCompletionDate: data.lastCompletionDate || null,

  // Start → finish
  runsStarted: data.runsStarted ?? 0,
  runsCompleted: data.runsCompleted ?? 0,
  runsInProgress: data.runsInProgress ?? 0,
  runsLeft: data.runsLeft ?? 0,
  completionRate: data.completionRate ?? 0,

  // Outcomes — the two kinds, kept apart
  avgScore: data.avgScore ?? null,
  scoredCompletions: data.scoredCompletions ?? 0,
  scoreBands: data.scoreBands || [],
  attachmentStyles: data.attachmentStyles || [],

  // Each entry is { title, kind, completions, users, unfinished, completionRate,
  // avgScore, lastDate, outcomes[] } — one questionnaire with its own mix.
  byAssessment: data.byAssessment || [],

  volumeSplit: data.volumeSplit || [],
  durationSplit: data.durationSplit || [],
  weekdayCompletions: data.weekdayCompletions || [],

  // Backend zero-fills the last 30 days; charts consume plain series.
  completionsTrend: (data.dailyTrend || []).map((day) => day.completions || 0),
  peopleTrend: (data.dailyTrend || []).map((day) => day.users || 0)
});

export const fetchCompanySelfAssessmentDashboard = async (department = '') => {
  try {
    const data = unwrap(await getCompanySelfAssessmentDashboard(department));
    return {
      ...adaptSelfAssessments(data),
      // Company-only: the size of the population, so the page can say
      // "48 of 240 employees have completed one".
      totalUsers: data.totalUsers
    };
  } catch (error) {
    console.error('fetchCompanySelfAssessmentDashboard error:', error);
    return null;
  }
};

// Matches the SelfAssessmentUsersTable fetcher contract.
export const fetchCompanySelfAssessmentUsers = async ({
  page = 1,
  perPage = 10,
  search = '',
  department = ''
} = {}) => {
  try {
    const data = unwrap(await getCompanySelfAssessmentUsers(page, perPage, search, department));
    return { data: data.data || [], totalCount: data.totalCount || 0 };
  } catch (error) {
    console.error('fetchCompanySelfAssessmentUsers error:', error);
    return { data: [], totalCount: 0 };
  }
};

/**
 * One employee's/member's assessment profile ({ user, scoreBands,
 * attachmentStyles, topAssessments, unfinished, completionHistory }). The
 * backend 404s any user outside this company, so no scoping is needed here.
 */
export const fetchCompanySelfAssessmentUserDetail = async (userId) => {
  try {
    return unwrap(await getCompanySelfAssessmentUserDetail(userId));
  } catch (error) {
    console.error('fetchCompanySelfAssessmentUserDetail error:', error);
    return null;
  }
};
